import {AsyncPipe, DecimalPipe} from '@angular/common';
import {Component, ElementRef, HostListener, Input, OnInit, ViewChild,} from '@angular/core';

import {MatIconModule} from '@angular/material/icon';
import {ActivatedRoute} from "@angular/router";

import {AnnotationService} from '@app/annotation/annotation.service';
import {Slot} from '@app/review/models';
import {PlaybackState, ReviewVideoService, Source} from '@app/review/review-video.service';
import {ReviewService} from '@app/review/review.service';
import {TimeRange} from '@app/review/timerange';
import {TimeScale} from '@app/review/timescale';
import {filter, map, Observable, tap} from 'rxjs';
import {take} from "rxjs/operators";


class AnnotationFrame {
    offset: number;
    hasDrawing: boolean;
}

// This is the shared transport chrome - one instance, always bound to whichever slot is currently active
// (review.component.html binds [slot]="activeSlot") - see docs/design/review-compare-assets.md §3.4.
@Component({
    selector: 'frog-review-timeline',
    imports: [MatIconModule, AsyncPipe, DecimalPipe],
    templateUrl: './review-timeline.component.html',
    styleUrl: './review-timeline.component.scss',
})
export class ReviewTimelineComponent implements OnInit {
    @Input() slot: Slot = Slot.Primary;
    @ViewChild('slider') slider: ElementRef<HTMLDivElement>;
    @ViewChild('buffer') buffer: ElementRef<HTMLCanvasElement>;

    public playbackState$: Observable<PlaybackState>;
    public buffered$: Observable<TimeRanges>;
    public annotations$: Observable<AnnotationFrame[]>;

    public offset: number;
    public frame;
    public sliderWidth: number;
    public bufferHeight: number;
    public startRangeOffset: number;
    public endRangeOffset: number;
    public scaledAnnotations: number[];
    public timeRange: TimeRange;

    private _playbackState: PlaybackState;
    private _ctx: CanvasRenderingContext2D;
    private _timeScale: TimeScale;
    private _mouseDown: boolean;
    private _mouseButton: number;
    private _origin: number;
    private _bufferGradient;

    constructor(
        private reviewService: ReviewService,  // only used to send ws notification data
        private reviewVideoService: ReviewVideoService,
        private annotationService: AnnotationService,
        private activatedRoute: ActivatedRoute,
    ) {
        this.bufferHeight = 4;
        this.startRangeOffset = -1;
        this.startRangeOffset = -1;
        this.scaledAnnotations = [];
        this._timeScale = null;
        this.timeRange = new TimeRange(null, null);
    }

    ngOnInit() {
        this.playbackState$ = this.reviewVideoService.playbackState$
            .pipe(
                filter((playbackState) => {
                    return playbackState.source !== Source.Timeline && playbackState.slot === this.slot;
                }),
                map((playbackState) => {
                    this._playbackState = playbackState;
                    this._timeScale = new TimeScale(playbackState.frameCount, playbackState.frameRate);

                    // Set TimeRange
                    this.timeRange = playbackState.playRange;
                    this.startRangeOffset = this.timeRange.left(this._timeScale);
                    this.endRangeOffset = this.timeRange.width(this._timeScale);

                    this.applyFrame(playbackState.frame);

                    return playbackState;
                }),
            );

        this.buffered$ = this.reviewVideoService.buffered$.pipe(
            tap((buffered) => {
                if (!this.slider) {
                    return;
                }

                this.sliderWidth = this.slider.nativeElement.offsetWidth;

                if (!this.buffer) {
                    return;
                }

                if (!this._ctx) {
                    this._ctx = this.buffer.nativeElement.getContext('2d');
                    this._bufferGradient = this._ctx.createLinearGradient(0,0, 0,this.bufferHeight);
                    this._bufferGradient.addColorStop(0, "#505e48ff");
                    this._bufferGradient.addColorStop(1, "#2a2e2700");
                }

                this._ctx.clearRect(0, 0, this.sliderWidth, this.bufferHeight);
                for (let i = 0; i < buffered.length; i++) {
                    const x =
                        (this._timeScale.timeToOffset(buffered.start(i)) / 100) * this.sliderWidth;
                    const w =
                        (this._timeScale.timeToOffset(buffered.end(i)) / 100) * this.sliderWidth;

                    this._ctx.fillStyle = this._bufferGradient;
                    this._ctx.fillRect(x, 0, w, this.bufferHeight);
                }
            }),
        );

        this.annotations$ = this.annotationService.annotations$
            .pipe(
                map((annotations) => {
                    // Scale the annotations, not in the template
                    return annotations.map((i) => {
                        const annotationFrame = new AnnotationFrame();
                        annotationFrame.offset = this._timeScale.timeToOffset(i.timestamp);
                        annotationFrame.hasDrawing = Boolean(i.drawing);
                        return annotationFrame;
                    });
                }),
            );

        this.activatedRoute.queryParamMap
            .pipe(
                map(i => parseInt(i.get('f'))),
                filter(frame => frame >= 0),
                take(1)
            )
            .subscribe(frame => {
                this.applyFrame(frame);
            })
    }

    public play(value: boolean = null, lockToPlayRange: boolean = true) {
        const isPlaying = value === null ? !this._playbackState.isPlaying : value;

        if (this.timeRange.isValid && lockToPlayRange && !this.timeRange.contains(this.frame, this._timeScale)) {
            const frame = this._timeScale.offsetToFrame(this.timeRange.left(this._timeScale) / 100);
            this.applyFrame(frame);
            this._playbackState.frame = frame;
        }

        this.reviewVideoService.play(Source.Timeline, isPlaying, this.slot);

        const data = {
            IsPlaying: isPlaying,
            Frame: this._playbackState.frame,
        };
        this.reviewService.sendActive(data);
    }

    // Mouse Drag
    public mouseDownHandler(event: MouseEvent) {
        event.preventDefault();
        this._mouseDown = true;
        this._mouseButton = event.button;
        this._origin = event.clientX - this.slider.nativeElement.offsetLeft;

        this.play(false, false);
    }

    public contextMenuHandler(event: Event) {
        event.preventDefault();
    }

    @HostListener('window:mouseup', ['$event'])
    public mouseUpHandler(event: MouseEvent) {
        this._mouseDown = false;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (this._mouseButton === 2) {
            // Range select, send to others
            const data = {
                PlayRange: this.timeRange.toArray(),
            };
            this.reviewService.sendActive(data);
        }
    }

    @HostListener('window:mousemove', ['$event'])
    public mouseMoveHandler(event: MouseEvent) {
        event.stopPropagation();

        if (!this._mouseDown) {
            return;
        }

        const x = event.clientX - this.slider.nativeElement.offsetLeft;
        const frame = this._timeScale.offsetToFrame(x / this.slider.nativeElement.offsetWidth);

        if (frame < 0 || frame > this._playbackState.frameCount) {
            return;
        }

        this.applyFrame(frame);

        this.reviewVideoService.setFrame(frame, Source.Timeline, this.slot);

        const data = {
            Frame: frame,
        };
        this.reviewService.sendActive(data);

        if (this._mouseButton === 2) {
            const originFrame = this._timeScale.offsetToFrame(
                this._origin / this.slider.nativeElement.offsetWidth,
            );
            const range = [originFrame, frame];
            if (originFrame > frame) {
                range.reverse();
            }
            this.timeRange = new TimeRange(
                this._timeScale.frameToTime(range[0]),
                this._timeScale.frameToTime(range[1]),
            );
            this.startRangeOffset = this.timeRange.left(this._timeScale);
            this.endRangeOffset = this.timeRange.width(this._timeScale);
            this.reviewVideoService.setRange(this.timeRange.toArray(), Source.Timeline, this.slot);
            // Send this range to others on mouse up
        }
    }

    @HostListener('window:keydown', ['$event'])
    keyDownEvent(event: KeyboardEvent) {
        let triggered = false;
        let multiplier = 1;

        const target = event.target as HTMLElement;
        if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
        ) {
            // User is editing a form control
            return;
        }

        // Play/pause
        if (event.key === 'k' || event.code === 'Space') {
            this.play();
            return;
        }

        if (event.key === '/') {
            this.clearTimeRange();
            return;
        }

        // Frame skips
        if (event.key === ',' || event.key === 'ArrowLeft') {
            triggered = true;
            multiplier = -1;
        }
        if (event.key === 'j') {
            triggered = true;
            multiplier = -5;
        }
        if (event.key === '.' || event.key === 'ArrowRight') {
            triggered = true;
            multiplier = 1;
        }
        if (event.key === 'l') {
            triggered = true;
            multiplier = 5;
        }

        if (triggered) {
            event.stopPropagation();
            event.preventDefault();

            this.play(false);

            let frame = this._playbackState.frame;
            frame += multiplier;

            this.applyFrame(frame);

            this.reviewVideoService.setFrame(frame, Source.Timeline, this.slot);
            const data = {
                Frame: frame,
            };
            this.reviewService.sendActive(data);
        }
    }

    // Apply State
    applyFrame(frame: number): void {
        this.frame = frame;
        if (!this._timeScale) {
            return;
        }
        this.offset = this._timeScale.frameToOffset(frame);

        if (this.timeRange.isValid && this._playbackState.isPlaying) {
            const rangeEndFrame = this._timeScale.offsetToFrame(
                this.timeRange.right(this._timeScale) / 100,
            );
            if (frame >= rangeEndFrame - 1) {
                const rangeStartFrame = this._timeScale.offsetToFrame(
                    this.timeRange.left(this._timeScale) / 100,
                );
                this.reviewVideoService.setFrame(rangeStartFrame, Source.Timeline, this.slot);
            }
        }
    }

    private clearTimeRange() {
        this.timeRange = new TimeRange(null, null);
        this.reviewVideoService.setRange(this.timeRange.toArray(), Source.All, this.slot);
        const data = {
            PlayRange: this.timeRange.toArray(),
        };
        this.reviewService.sendActive(data);
    }
}
